/**
 * A caixa "Nova Categoria" de Cadastros.
 *
 * Papel: reproduz `#modal-cat` e `saveCatModal` (l. 1115–1148, 5876–5921):
 * "Nome da Categoria" com o foco ao abrir, "Cor" com a cor padrão, e as
 * subcategorias como fichas — o "+ Adicionar" e o Enter acrescentam, a
 * repetida é ignorada e o "✕" de cada ficha a tira.
 *
 * Como usar: quem monta passa o plano, para recusar o nome que já existe, e
 * desmonta a caixa em `onFechar`. A categoria nasce de natureza despesa e não é
 * de sistema. A validação é a do `novaCategoriaSchema`, a mesma da API, e as
 * frases são as da origem.
 */
import {
  COR_PADRAO_DA_CATEGORIA,
  type Categoria,
  novaCategoriaSchema,
} from '@concierge/shared';
import { useRef, useState } from 'react';

import { useNotificacao } from '@/shared/embutido';
import { cn } from '@/shared/lib/utils';
import { useCriarCategoria } from '../hooks/usar-financas';
import { Botao } from './kit/botao';
import { CaixaDeDialogo } from './kit/caixa-de-dialogo';
import { CLASSE_DO_CONTROLE, Campo } from './kit/campo';
import { Ficha } from './kit/ficha';

interface ModalNovaCategoriaProps {
  plano: readonly Categoria[];
  onFechar: () => void;
}

/**
 * A caixa "Nova Categoria".
 *
 * @param plano - O plano de contas.
 * @param onFechar - Desmonta a caixa.
 * @returns A caixa de 460 px.
 */
export function ModalNovaCategoria({ plano, onFechar }: ModalNovaCategoriaProps) {
  const notificar = useNotificacao();
  const criar = useCriarCategoria();
  const [nome, setNome] = useState('');
  const [cor, setCor] = useState(COR_PADRAO_DA_CATEGORIA);
  const [subcategoria, setSubcategoria] = useState('');
  const [fichas, setFichas] = useState<string[]>([]);
  const campo = useRef<HTMLInputElement>(null);

  /**
   * Acrescenta o nome digitado às fichas; vazio ou repetido é ignorado. Nos
   * três casos o foco volta ao campo, para digitar a próxima (l. 5877–5884).
   */
  function handleAdicionar() {
    const aparado = subcategoria.trim();
    campo.current?.focus();
    if (aparado === '' || fichas.includes(aparado)) return;
    setFichas((anteriores) => [...anteriores, aparado]);
    setSubcategoria('');
  }

  /** Valida, grava e avisa. */
  async function handleSalvar() {
    const corpo = novaCategoriaSchema.safeParse({ nome, cor, subcategorias: fichas });
    if (!corpo.success) {
      notificar(corpo.error.issues[0]?.message ?? 'Informe o nome da categoria', false);
      return;
    }
    if (plano.some((categoria) => categoria.nome === corpo.data.nome)) {
      notificar('Categoria já existe', false);
      return;
    }
    try {
      await criar.mutateAsync(corpo.data);
      onFechar();
      const quantas = corpo.data.subcategorias.length;
      notificar(
        `✓ Categoria "${corpo.data.nome}" criada${quantas > 0 ? ` com ${String(quantas)} subcategoria(s)` : ''}`,
      );
    } catch (erro) {
      notificar(erro instanceof Error ? erro.message : 'Não foi possível gravar.', false);
    }
  }

  return (
    <CaixaDeDialogo
      titulo="Nova Categoria"
      largura={460}
      onFechar={onFechar}
      rodape={
        <>
          <Botao variante="fantasma" onClick={onFechar}>
            Cancelar
          </Botao>
          <Botao onClick={() => void handleSalvar()} disabled={criar.isPending}>
            Salvar Categoria
          </Botao>
        </>
      }
    >
      <Campo rotulo="Nome da Categoria" id="categoria-nome">
        <input
          id="categoria-nome"
          // A origem põe o foco no nome ao abrir (l. 5886).
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus
          placeholder="Ex: Segurança, Jardinagem..."
          value={nome}
          onChange={(evento) => setNome(evento.target.value)}
          className={CLASSE_DO_CONTROLE}
        />
      </Campo>
      <Campo rotulo="Cor" id="categoria-cor">
        <div className="flex items-center gap-2.5">
          <input
            id="categoria-cor"
            type="color"
            value={cor}
            onChange={(evento) => setCor(evento.target.value)}
            className="h-9 w-12 cursor-pointer rounded-[6px] border border-emb-borda p-0.5"
          />
          <span className="text-[12px] text-texto-fraco">
            Escolha uma cor para identificar a categoria
          </span>
        </div>
      </Campo>
      <Campo rotulo="Subcategorias" id="categoria-subcategoria">
        <div className="flex gap-1.5">
          <input
            ref={campo}
            id="categoria-subcategoria"
            placeholder="Nome da subcategoria..."
            value={subcategoria}
            onChange={(evento) => setSubcategoria(evento.target.value)}
            onKeyDown={(evento) => {
              if (evento.key === 'Enter') {
                evento.preventDefault();
                handleAdicionar();
              }
            }}
            // A borda continua bege no foco: a origem a escreve no `style` do
            // campo, que vence o `:focus` de `.field`; só o anel aparece.
            className={cn(CLASSE_DO_CONTROLE, 'flex-1 py-[7px] focus:border-emb-borda')}
          />
          <Botao variante="fantasma" tamanho="sm" onClick={handleAdicionar}>
            + Adicionar
          </Botao>
        </div>
        <div className="mt-2 flex min-h-7 flex-wrap gap-1.5">
          {fichas.map((ficha) => (
            <Ficha
              key={ficha}
              texto={ficha}
              onRemover={() =>
                setFichas((anteriores) => anteriores.filter((item) => item !== ficha))
              }
            />
          ))}
        </div>
      </Campo>
    </CaixaDeDialogo>
  );
}
